import React, { useState } from "react";
import { MagnifyingGlass, CircleNotch, ShieldCheck } from "@phosphor-icons/react";
import { PageContainer } from "@/components/customer/PageContainer";
import { api, getErrorMessage } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";
import { ASSETS } from "@/lib/assets";

const STATUS_STYLES = {
  "approved": "bg-[#73bf44]/15 text-[#73bf44] border-[#73bf44]/30",
  "completed": "bg-teal-500/15 text-teal-300 border-teal-500/30",
  "declined": "bg-red-500/15 text-red-400 border-red-500/30",
  "further information required": "bg-orange-500/15 text-orange-400 border-orange-500/30",
  "invoiced": "bg-teal-500/15 text-teal-300 border-teal-500/30",
  "pending assessment": "bg-neutral-500/15 text-neutral-300 border-neutral-500/40",
};
const statusStyle = (s) => STATUS_STYLES[(s || "").toLowerCase()] || "bg-sky-500/15 text-sky-300 border-sky-500/30";

function fmtDate(iso) {
  if (!iso) return "an unknown date";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

export default function ClaimStatus() {
  const { caravan } = useCustomer();
  const [chassis, setChassis] = useState(caravan?.chassis_number || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {chassis, count, claims}

  const lookup = async () => {
    const ch = chassis.trim();
    if (!ch || loading) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get(`/warranty/status`, { params: { chassis: ch }, timeout: 30000 });
      setResult(data);
    } catch (err) {
      setResult({ error: getErrorMessage(err, "We couldn't check your claim right now. Please try again shortly.") });
    } finally {
      setLoading(false);
    }
  };

  const summary = () => {
    if (!result || result.error) return "";
    const { chassis: ch, count, claims } = result;
    if (count === 0) {
      return `I couldn't find any warranty claims under ${ch}. If you've only just submitted one it can take a little while to come through — otherwise please double-check your chassis number.`;
    }
    if (count === 1) {
      const c = claims[0];
      const appr = c.status.toLowerCase() === "approved" && c.approval_number
        ? ` Your approval number is ${c.approval_number}.` : "";
      return `I can see 1 claim under ${ch} — submitted on ${fmtDate(c.submitted_on)} and currently marked "${c.status}".${appr}`;
    }
    return `I can see ${count} claims under ${ch}:`;
  };

  return (
    <PageContainer
      overline="Warranty"
      title="Track my claim"
      subtitle="Enter your OTM chassis number and I'll tell you the status of any warranty claim on file. For your privacy, only the submission date and status are shown here."
    >
      <div className="max-w-2xl space-y-6" data-testid="claim-status-page">
        <section className="bg-[#141414] border border-neutral-800 rounded-2xl p-6">
          <label className="text-sm text-neutral-300 mb-1.5 block">OTM Chassis Number</label>
          <div className="flex gap-2">
            <input
              value={chassis}
              onChange={(e) => setChassis(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              placeholder="e.g. OTM01526"
              className="w-full bg-black/40 border border-neutral-700 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[#73bf44] transition-colors placeholder:text-neutral-600"
              data-testid="claim-status-chassis"
            />
            <button
              onClick={lookup}
              disabled={loading || !chassis.trim()}
              data-testid="claim-status-lookup"
              className="shrink-0 inline-flex items-center gap-2 bg-[#73bf44] text-[#050505] rounded-lg font-semibold px-5 text-sm hover:bg-[#5fa338] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <CircleNotch size={16} className="animate-spin" /> : <MagnifyingGlass size={16} weight="bold" />}
              {loading ? "Checking…" : "Check status"}
            </button>
          </div>
        </section>

        {result && (
          <section className="flex gap-3" data-testid="claim-status-result">
            <div className="w-10 h-10 rounded-full border border-[#262626] overflow-hidden shrink-0 mt-0.5">
              <img src={ASSETS.nick} alt="Nick" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 bg-[#141414] border border-neutral-800 rounded-2xl rounded-tl-md p-5">
              {result.error ? (
                <p className="text-sm text-red-300" data-testid="claim-status-error">{result.error}</p>
              ) : (
                <>
                  <p className="text-sm text-neutral-200 leading-relaxed" data-testid="claim-status-summary">{summary()}</p>
                  {result.count > 0 && (
                    <div className="mt-4 space-y-2.5" data-testid="claim-status-list">
                      {result.claims.map((c, i) => (
                        <div key={i} className="flex flex-col gap-2 bg-black/30 border border-neutral-800 rounded-xl px-4 py-3" data-testid={`claim-status-item-${i}`}>
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2.5 text-sm text-neutral-300">
                              <ShieldCheck size={18} className="text-[#73bf44]" weight="duotone" />
                              <span>Submitted <span className="text-white font-medium">{fmtDate(c.submitted_on)}</span></span>
                            </div>
                            <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${statusStyle(c.status)}`}>{c.status}</span>
                          </div>
                          {c.status.toLowerCase() === "approved" && c.approval_number && (
                            <div className="flex items-center gap-2 text-xs text-neutral-400 pl-[26px]" data-testid={`claim-status-approval-${i}`}>
                              <span>Approval number:</span>
                              <span className="font-mono text-[#73bf44] font-semibold">{c.approval_number}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-neutral-600 mt-4">
                    Need more detail or think something's not right? Head to Contact Support and our warranty team can help.
                  </p>
                </>
              )}
            </div>
          </section>
        )}
      </div>
    </PageContainer>
  );
}
